import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, CheckCircle, XCircle } from "lucide-react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import apiService from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";

const CreateInvoice = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();

  // Order data
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  // Selected delivery orders
  const [selectedDeliveryOrders, setSelectedDeliveryOrders] = useState([]);

  // Invoice form fields
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: "",
    invoiceDate: format(new Date(), "yyyy-MM-dd"),
    dueDate: format(
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      "yyyy-MM-dd"
    ),
    paymentTerms: "Net 30 Days",
    placeOfSupply: "",
    transportMode: "Road",
    vehicleNumber: "",
    dateOfSupply: format(new Date(), "yyyy-MM-dd"),
    reverseCharge: "No",
    notes: "",
    termsAndConditions: `1. Payment due within 30 days of invoice date.
2. Interest @24% p.a. will be charged on overdue amounts.
3. All disputes subject to [City] jurisdiction only.
4. Goods once sold will not be taken back.
5. Delivery challan to be returned duly signed.
6. All prices are excluding applicable taxes unless specified.`,
  });

  // Fetch order details
  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const orderData = await apiService.getOrder(orderId);

      setOrder(orderData);

      // Auto-select uninvoiced AND delivered delivery orders only
      const uninvoicedDOs = (orderData.deliveryOrders || [])
        .filter((do_) => !do_.invoiced && do_.deliveryStatus === "delivered")
        .map((do_) => do_.id);

      setSelectedDeliveryOrders(uninvoicedDOs);
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  // Calculate invoice totals based on selected delivery orders
  const calculateInvoiceTotals = () => {
    if (!order || selectedDeliveryOrders.length === 0) {
      return {
        items: [],
        subtotal: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        totalAmount: 0,
        amountInWords: "",
      };
    }

    // Get selected delivery orders
    const selectedDOs = order.deliveryOrders.filter((do_) =>
      selectedDeliveryOrders.includes(do_.id)
    );

    // Combine items from all selected delivery orders
    const allDeliveryItems = [];
    selectedDOs.forEach((do_) => {
      if (do_.items) {
        allDeliveryItems.push(...do_.items);
      }
    });

    // Calculate items with proper tax breakdown
    const items = allDeliveryItems.map((item, index) => {
      // Get the corresponding order item for pricing and product details
      const orderItem =
        item.orderItem ||
        order.orderItems?.find(
          (oi) =>
            oi.productSkuId === item.orderItem?.productSkuId ||
            oi.id === item.orderItemId
        );

      const unitPrice = orderItem?.price || orderItem?.unitPrice || 0;
      const quantity = item.quantity || 1;
      const taxableValue = unitPrice * quantity;
      const gstRate = orderItem?.productSku?.product?.gstPercent || 18;

      // For same state transactions (CGST + SGST), for interstate (IGST)
      const isInterstate = false; // Determine based on customer state

      let cgstAmount = 0,
        sgstAmount = 0,
        igstAmount = 0;

      if (isInterstate) {
        igstAmount = (taxableValue * gstRate) / 100;
      } else {
        cgstAmount = (taxableValue * gstRate) / 200;
        sgstAmount = (taxableValue * gstRate) / 200;
      }

      const totalAmount = taxableValue + cgstAmount + sgstAmount + igstAmount;

      return {
        srNo: index + 1,
        productSkuId: item.orderItem?.productSkuId,
        description:
          item.orderItem?.productSku?.name ||
          item.orderItem?.productSku?.description ||
          item.orderItem?.productSku?.product?.description ||
          `Product ${item.productId}`,
        barcode: item.orderItem?.productSku?.barcode || "N/A",
        sku: item.orderItem?.productSku?.sku || "N/A",
        hsnCode: item.orderItem?.productSku?.product?.hsCode || "9999",
        unit: "PCS",
        quantity,
        rate: unitPrice,
        taxableValue,
        gstRate,
        cgstRate: isInterstate ? 0 : gstRate / 2,
        cgstAmount,
        sgstRate: isInterstate ? 0 : gstRate / 2,
        sgstAmount,
        igstRate: isInterstate ? gstRate : 0,
        igstAmount,
        totalAmount,
      };
    });

    // Calculate totals
    const subtotal = items.reduce((sum, item) => sum + item.taxableValue, 0);
    const cgstAmount = items.reduce((sum, item) => sum + item.cgstAmount, 0);
    const sgstAmount = items.reduce((sum, item) => sum + item.sgstAmount, 0);
    const igstAmount = items.reduce((sum, item) => sum + item.igstAmount, 0);
    const totalTax = cgstAmount + sgstAmount + igstAmount;
    const totalBeforeRounding = subtotal + totalTax;
    const totalAmount = Math.round(totalBeforeRounding);
    const roundOff = totalAmount - totalBeforeRounding;

    return {
      items,
      subtotal,
      cgstAmount,
      sgstAmount,
      igstAmount,
      roundOff,
      totalAmount,
      amountInWords: convertToWords(totalAmount),
    };
  };

  // Convert number to words for Indian currency
  const convertToWords = (amount) => {
    const units = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const teens = [
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    if (amount === 0) return "Zero Rupees Only";

    const numToWords = (num) => {
      if (num < 10) return units[num];
      if (num < 20) return teens[num - 10];
      if (num < 100)
        return (
          tens[Math.floor(num / 10)] + (num % 10 ? " " + units[num % 10] : "")
        );
      if (num < 1000)
        return (
          units[Math.floor(num / 100)] +
          " Hundred" +
          (num % 100 ? " " + numToWords(num % 100) : "")
        );
      if (num < 100000)
        return (
          numToWords(Math.floor(num / 1000)) +
          " Thousand" +
          (num % 1000 ? " " + numToWords(num % 1000) : "")
        );
      if (num < 10000000)
        return (
          numToWords(Math.floor(num / 100000)) +
          " Lakh" +
          (num % 100000 ? " " + numToWords(num % 100000) : "")
        );
      return (
        numToWords(Math.floor(num / 10000000)) +
        " Crore" +
        (num % 10000000 ? " " + numToWords(num % 10000000) : "")
      );
    };

    return numToWords(Math.floor(amount)) + " Rupees Only";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedDeliveryOrders.length === 0) {
      toast.error("Please select at least one delivery order");
      return;
    }

    try {
      setCreatingInvoice(true);

      const totals = calculateInvoiceTotals();

      // Get selected delivery orders
      const selectedDOs = order.deliveryOrders.filter((do_) =>
        selectedDeliveryOrders.includes(do_.id)
      );

      // Format delivery order numbers
      const doNumbers = selectedDOs.map((do_) => do_.deliveryNumber).join(", ");
      const doDate =
        selectedDOs.length > 0 && selectedDOs[0].actualDeliveryDate
          ? format(new Date(selectedDOs[0].actualDeliveryDate), "yyyy-MM-dd")
          : "";

      // Prepare invoice data for API
      const invoiceDataForAPI = {
        orderId: order.id,
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceDate: invoiceData.invoiceDate,
        dueDate: invoiceData.dueDate,
        paymentTerms: invoiceData.paymentTerms,
        notes: invoiceData.notes,
        subtotal: totals.subtotal,
        taxAmount: totals.cgstAmount + totals.sgstAmount + totals.igstAmount,
        totalAmount: totals.totalAmount,
        deliveryOrderIds: selectedDeliveryOrders,
        deliveryOrderNumber: doNumbers,
        deliveryOrderDate: doDate,
        items: totals.items.map((item) => ({
          productSkuId: item.productSkuId,
          quantity: item.quantity,
          unitPrice: item.rate,
          totalPrice: item.totalAmount,
          gstPercent: item.gstRate,
          gstAmount: item.cgstAmount + item.sgstAmount + item.igstAmount,
        })),
      };

      // Create invoice via API
      const response = await apiService.createInvoice(invoiceDataForAPI);

      toast.success("Invoice created successfully!");

      // Navigate back to orders page
      navigate("/admin/orders");
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error(error.message || "Failed to create invoice");
    } finally {
      setCreatingInvoice(false);
    }
  };

  const toggleDeliveryOrder = (doId) => {
    setSelectedDeliveryOrders((prev) =>
      prev.includes(doId) ? prev.filter((id) => id !== doId) : [...prev, doId]
    );
  };

  const toggleAllDeliveryOrders = () => {
    const uninvoicedDOs = (order?.deliveryOrders || [])
      .filter((do_) => !do_.invoiced && do_.deliveryStatus === "delivered")
      .map((do_) => do_.id);

    if (selectedDeliveryOrders.length === uninvoicedDOs.length) {
      setSelectedDeliveryOrders([]);
    } else {
      setSelectedDeliveryOrders(uninvoicedDOs);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!order) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">Order not found</div>
      </div>
    );
  }

  const totals = calculateInvoiceTotals();
  const allDOs = order.deliveryOrders || [];
  const uninvoicedDOs = allDOs.filter(
    (do_) => !do_.invoiced && do_.deliveryStatus === "delivered"
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 shadow-sm border-b border-gray-200 dark:border-slate-700">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/orders")}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Create Invoice
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Order: {order.poNumber || order.orderNumber} •{" "}
                  {order.customer?.businessName || order.customer?.name}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Delivery Order Selection */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Select Delivery Orders
              </h2>
              {uninvoicedDOs.length > 1 && (
                <button
                  type="button"
                  onClick={toggleAllDeliveryOrders}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {selectedDeliveryOrders.length === uninvoicedDOs.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              )}
            </div>

            {uninvoicedDOs.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No delivered delivery orders available for invoicing</p>
              </div>
            ) : (
              <div className="space-y-3">
                {uninvoicedDOs.map((deliveryOrder) => {
                  const isInvoiced = deliveryOrder.invoiced;
                  const isSelected = selectedDeliveryOrders.includes(
                    deliveryOrder.id
                  );

                  return (
                    <label
                      key={deliveryOrder.id}
                      className={`flex items-start gap-4 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        isInvoiced
                          ? "bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 opacity-60 cursor-not-allowed"
                          : isSelected
                          ? "bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500"
                          : "bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-600"
                      }`}
                    >
                      <div className="flex items-center pt-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isInvoiced}
                          onChange={() => toggleDeliveryOrder(deliveryOrder.id)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {deliveryOrder.deliveryNumber}
                          </span>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              isInvoiced
                                ? "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                                : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                            }`}
                          >
                            {isInvoiced ? "Already Invoiced" : "Available"}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div>
                            <span className="font-medium">Delivery Date:</span>{" "}
                            {deliveryOrder.actualDeliveryDate
                              ? format(
                                  new Date(deliveryOrder.actualDeliveryDate),
                                  "dd MMM yyyy"
                                )
                              : deliveryOrder.expectedDeliveryDate
                              ? format(
                                  new Date(deliveryOrder.expectedDeliveryDate),
                                  "dd MMM yyyy (Expected)"
                                )
                              : "N/A"}
                          </div>
                          <div>
                            <span className="font-medium">Items:</span>{" "}
                            {deliveryOrder.items?.length || 0} items
                          </div>
                          <div>
                            <span className="font-medium">Status:</span>{" "}
                            <span
                              className={`capitalize px-2 py-0.5 rounded text-xs ${
                                deliveryOrder.deliveryStatus === "delivered"
                                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                  : deliveryOrder.deliveryStatus ===
                                    "dispatched"
                                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                  : deliveryOrder.deliveryStatus ===
                                    "processing"
                                  ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                                  : "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400"
                              }`}
                            >
                              {deliveryOrder.deliveryStatus}
                            </span>
                          </div>
                          {deliveryOrder.deliveryMethod && (
                            <div>
                              <span className="font-medium">Method:</span>{" "}
                              <span className="capitalize">
                                {deliveryOrder.deliveryMethod}
                              </span>
                            </div>
                          )}
                        </div>

                        {deliveryOrder.items &&
                          deliveryOrder.items.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-700">
                              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                Items in this delivery:
                              </div>
                              <div className="space-y-1">
                                {deliveryOrder.items
                                  .slice(0, 3)
                                  .map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="text-xs text-gray-600 dark:text-gray-400"
                                    >
                                      •{" "}
                                      {item.orderItem?.productSku?.name ||
                                        "Product"}{" "}
                                      - Qty: {item.quantity}
                                    </div>
                                  ))}
                                {deliveryOrder.items.length > 3 && (
                                  <div className="text-xs text-gray-500 dark:text-gray-500">
                                    + {deliveryOrder.items.length - 3} more
                                    items
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {selectedDeliveryOrders.length === 0 &&
              uninvoicedDOs.length > 0 && (
                <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                  <p className="text-sm text-orange-700 dark:text-orange-400">
                    Please select at least one delivery order to create an
                    invoice.
                  </p>
                </div>
              )}
          </div>

          {/* Invoice Summary */}
          {selectedDeliveryOrders.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Invoice Summary
              </h2>

              <div className="space-y-4">
                {/* Invoice Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Invoice Date
                    </label>
                    <input
                      type="date"
                      value={invoiceData.invoiceDate}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          invoiceDate: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={invoiceData.dueDate}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          dueDate: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                      required
                    />
                  </div>
                </div>

                {/* Payment Terms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Payment Terms
                  </label>
                  <input
                    type="text"
                    value={invoiceData.paymentTerms}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        paymentTerms: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                    placeholder="e.g., Net 30 Days"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={invoiceData.notes}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, notes: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                    placeholder="Additional notes for this invoice..."
                  />
                </div>

                {/* Amount Breakdown */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      Subtotal:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ₹{totals.subtotal.toFixed(2)}
                    </span>
                  </div>
                  {totals.cgstAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        CGST:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        ₹{totals.cgstAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {totals.sgstAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        SGST:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        ₹{totals.sgstAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {totals.igstAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        IGST:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        ₹{totals.igstAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-blue-200 dark:border-blue-800 pt-2 flex justify-between">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      Total Amount:
                    </span>
                    <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                      ₹{totals.totalAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 italic">
                    {totals.amountInWords}
                  </div>
                </div>

                {/* Items List */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Invoice Items ({totals.items.length})
                  </h3>
                  <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                      <thead className="bg-gray-50 dark:bg-slate-900">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                            #
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                            Product
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                            Qty
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                            Rate
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                        {totals.items.map((item) => (
                          <tr key={item.srNo}>
                            <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                              {item.srNo}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                              {item.description}
                            </td>
                            <td className="px-3 py-2 text-sm text-right text-gray-900 dark:text-white">
                              {item.quantity}
                            </td>
                            <td className="px-3 py-2 text-sm text-right text-gray-900 dark:text-white">
                              ₹{item.rate.toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-sm text-right font-medium text-gray-900 dark:text-white">
                              ₹{item.totalAmount.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <button
              type="button"
              onClick={() => navigate("/admin/orders")}
              className="px-6 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingInvoice || selectedDeliveryOrders.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {creatingInvoice ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating Invoice...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  Create Invoice
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInvoice;
